import { PLATFORM } from 'aurelia-framework';
import {
	activationStrategy,
	RouteConfig as AureliaRouteConfig,
} from 'aurelia-router';

interface AppRouteSettings {
	data?: {
		/**
		 * isLoginPage is used to determine whether a user should be redirected away from it
		 * if they're already logged in. This is utilized by the "AuthorizeStep" pipeline.
		 */
		isLoginPage?: boolean;
		/**
		 * hideNavigation will remove the navigation bar for forms with this set to true.
		 * This is utilized by the "login" page.
		 */
		hideNavigation?: boolean;
		/**
		 * hideOnFormCatalog will make the form not appear in the list of forms
		 * on the "form-catalogue" page.
		 *
		 * @link http://localhost:9000/#/form-catalogue
		 */
		hideOnFormCatalog?: boolean;
	};
	roles?: string[];
}

interface AppRouteParams {
	appLayout?: boolean;
	href?: string;
	layoutViewModel?: string;
	moduleId: string;
	name?: string;
	nav?: boolean;
	route: string | string[];
	settings?: AppRouteSettings;
	title?: string;
}

/**
 * RouteConfig is this projects extension of Aurelia's RouteConfig.
 */
export interface RouteConfig extends AureliaRouteConfig {
	hideOnFormCatalog?: boolean;
	/**
	 * Data attached to the route at configuration time.
	 * @override
	 */
	settings?: AppRouteSettings;
}

const createAppRoute = (params: AppRouteParams): RouteConfig => {
	const mergedRouteParams: AppRouteParams = {
		// Defaults
		appLayout: true,
		href: '',
		moduleId: '',
		name: '',
		nav: true,
		route: '',
		settings: {},
		title: '',
		// Override defaults
		...params,
	};
	// NOTE: Jake: 2019
	// Add defaults for unset data in objects, we do this so that if
	// a route passed in with only the "data" settings, the roles are automatically
	// applied if "undefined".
	// ie. customisation-editor
	if (mergedRouteParams.settings.roles === undefined) {
		// Default behaviour requires user logs in.
		mergedRouteParams.settings.roles = ['LoginUser'];
	}

	const { appLayout, href, moduleId, name, nav, route, settings, title } =
		mergedRouteParams;

	const endRoute = (typeof route === 'string' ? route : route.reverse()[0])
		.split('/')
		.reverse()
		.find((r) => r[0] !== ':');
	const routeParams: RouteConfig = {
		activationStrategy: activationStrategy.replace,
		layoutViewModel: appLayout
			? PLATFORM.moduleName('layouts/main-content')
			: undefined,
		moduleId,
		name: name || endRoute,
		nav,
		route,
		settings,
		title: title || `route-title.${endRoute}`,
	};
	if (href) {
		routeParams.href = href;
	}
	return routeParams;
};

export const FORM_SUBTYPE = 'formSubtype';

/**
 * AppRouteID is a list of route names that need to be both referenced in this file
 * and in other files, such as for generating URLs from the name.
 */

export const enum AppRouteID {
	Login = 'login',
	SsoLogin = 'sso-login',
	CustomisationEditor = 'customisation-editor',
	AdviserDetails = 'adviser-details',
	DealerDetails = 'dealer-details',
	InterestRateHistory = 'interest-rate-history',
	MemberDetails = 'member-and-client-details',
	MemberBeneficiaries = 'member-beneficiaries',
	PayrollDetails = 'payroll-details',
	PayrollLocation = 'payroll-location',
	PayrollContact = 'payroll-contact',
	PayrollCenter = 'payroll-center',
	LedgersByTransaction = 'ledgers-by-transaction',
	JournalsByTransaction = 'journals-by-transaction',
	TransactionHeaders = 'transaction-headers',
	TransactionLines = 'transaction-lines',
	TransactionMessages = 'transaction-messages',
	TransactionCentre = 'transaction-centre',
	Transaction60 = 'transaction-60',
	Transaction61 = 'transaction-61',
	Transaction85 = 'transaction-85',
	Transaction62 = 'transaction-62',
	Transaction77 = 'transaction-77',
	Transaction98 = 'transaction-98',
	Transaction8U = 'transaction-8U',
	Transaction8S = 'transaction-8S',
	PensionRedemptionInvestmentProfile = 'pension-redemption-investment-profile',
	PersonalDetails = 'personal-details',
	WorkerDashboard = 'worker-dashboard',
	ServerDashboard = 'server-dashboard',
	RedemptionInvestmentProfile = 'redemption-investment-profile',
	SupplementaryDetails = 'supplementary-details',
	ReportingCentre = 'reporting-centre',
	Report = 'report',
	FamilyLawSplitDetails = 'family-law-split-details',
	ClientGroups = 'client-groups',
	UserDetails = 'user-details',
	WebUserSecurity = 'web-user-security',
	AssetTransactionList = 'asset-transaction-list',
	MemberBenefitQuotes = 'member-benefit-quotes',
	NewReceipt = 'new-receipt',
	ReceiptRefundDetails = 'receipt-refund-details',
	MemberPaymentDeductionHistory = 'member-payment-deduction-history',
	MemberPaymentScheduleHistory = 'member-payment-schedule-history',
	MemberCorrespondenceRegister = 'member-correspondence-register',
	RecordAuthorisations = 'record-authorisations',
	MergeHistory = 'merge-history',
	ChequeEftInformation = 'cheque-eft-information',
	PayrollRatesHistory = 'payroll-rates-history',
	TreatyExpenseCodes = 'treaty-expense-codes',
	TreatyConfiguration = 'treaty-configuration',
	VerificationHistory = 'verification-history',
	ContributionFiles = 'contribution-files',
	StaticDataLines = 'static-data-lines',
	WorkbenchLines = 'workbench-lines',
	ContributionLines = 'contribution-lines',
	AddressDetails = 'address-details',
	ErrorsAndExceptions = 'errors-and-exceptions',
	ExceptionHistory = 'exception-history',
	MemberPaymentHistory = 'member-payment-history',
	TransferRequestLines = 'transfer-request-lines',
	InvestmentHistory = 'investment-history',
	RedemptionHistory = 'redemption-history',
	SuperStreamManualMatches = 'superstream-manual-matches',
	UnitPriceHistory = 'unit-price-history',
	DealerHistory = 'dealer-history',
	AdviserHistory = 'adviser-history',
	CorporateActionElections = 'corporate-action-elections',
	IncomeElectionHistory = 'income-election-history',
	CorporateAction = 'corporate-action',
	ApiAudit = 'api-audit',
	SuppressionDetails = 'suppression-details',
	SuppressionIssues = 'suppression-issues',
	DataRetentionControl = 'data-retention-control',
	ClientDeidentification = 'client-deidentification',
	DataRetentionProcessing = 'data-retention-processing',
	DeidentifiedClients = 'deidentified-clients',
	Blank = 'blank',
	Credentials = 'credentials',
	BankStatementReconciliation = 'bank-statement-reconciliation',
	BatchProcessingCentre = 'batch-processing-centre',
	BenefitsPaymentsCentre = 'benefits-payments-centre',
}

export const AppRoutes = [
	createAppRoute({
		appLayout: false,
		nav: false,
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('login/login')
			: PLATFORM.moduleName('login/login', 'login/login'),
		route: AppRouteID.Login,
		settings: {
			data: {
				isLoginPage: true,
				hideNavigation: true,
				hideOnFormCatalog: true,
			},
			// Explicitly disable "LoginUser" rule.
			roles: [],
		},
	}),
	createAppRoute({
		appLayout: false,
		nav: false,
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('login/okta-login')
			: PLATFORM.moduleName('login/okta-login', 'login/okta-login'),
		route: AppRouteID.SsoLogin,
		settings: {
			data: {
				hideNavigation: true,
				hideOnFormCatalog: true,
			},
		},
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/client-summary/client-summary')
			: PLATFORM.moduleName(
					'forms/client-summary/client-summary',
					'forms/client-summary/client-summary',
			  ),
		route: 'client-summary',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/ato-interfaces/ato-interfaces')
			: PLATFORM.moduleName(
					'forms/ato-interfaces/ato-interfaces',
					'forms/ato-interfaces/ato-interfaces',
			  ),
		route: 'ato-interfaces',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/annual-review-history/annual-review-history',
			  )
			: PLATFORM.moduleName(
					'forms/annual-review-history/annual-review-history',
					'forms/annual-review-history/annual-review-history',
			  ),
		route: 'annual-review-history',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/bank-account/bank-account')
			: PLATFORM.moduleName(
					'forms/bank-account/bank-account',
					'forms/bank-account/bank-account',
			  ),
		route: 'bank-account',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/bank-account/global-bank-account')
			: PLATFORM.moduleName(
					'forms/bank-account/global-bank-account',
					'forms/bank-account/global-bank-account',
			  ),
		route: 'global-bank-account',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/bank-account/off-system-bank-account')
			: PLATFORM.moduleName(
					'forms/bank-account/off-system-bank-account',
					'forms/bank-account/off-system-bank-account',
			  ),
		route: 'off-system-bank-account',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/correspondence-register/payroll-correspondence-register',
			  )
			: PLATFORM.moduleName(
					'forms/correspondence-register/payroll-correspondence-register',
					'forms/correspondence-register/payroll-correspondence-register',
			  ),
		route: 'payroll-correspondence-register',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/correspondence-register/member-correspondence-register',
			  )
			: PLATFORM.moduleName(
					'forms/correspondence-register/member-correspondence-register',
					'forms/correspondence-register/member-correspondence-register',
			  ),
		route: 'member-correspondence-register',
	}),
	createAppRoute({
		href: AppRouteID.MemberCorrespondenceRegister,
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/correspondence-register/member-correspondence-register',
			  )
			: PLATFORM.moduleName(
					'forms/correspondence-register/member-correspondence-register',
					'forms/correspondence-register/member-correspondence-register',
			  ),
		route: `${AppRouteID.MemberCorrespondenceRegister}/:${FORM_SUBTYPE}`,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/correspondence-register/adviser-correspondence-register',
			  )
			: PLATFORM.moduleName(
					'forms/correspondence-register/adviser-correspondence-register',
					'forms/correspondence-register/adviser-correspondence-register',
			  ),
		route: 'adviser-correspondence-register',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/bulk-address-verification/bulk-address-verification',
			  )
			: PLATFORM.moduleName(
					'forms/bulk-address-verification/bulk-address-verification',
					'forms/bulk-address-verification/bulk-address-verification',
			  ),
		route: 'bulk-address-verification',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/category-description/category-description',
			  )
			: PLATFORM.moduleName(
					'forms/category-description/category-description',
					'forms/category-description/category-description',
			  ),
		route: 'category-description',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/adviser-authority-details/adviser-authority-details',
			  )
			: PLATFORM.moduleName(
					'forms/adviser-authority-details/adviser-authority-details',
					'forms/adviser-authority-details/adviser-authority-details',
			  ),
		route: 'adviser-authority-details',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/adviser-authority-details/fee-adviser-authority-details',
			  )
			: PLATFORM.moduleName(
					'forms/adviser-authority-details/fee-adviser-authority-details',
					'forms/adviser-authority-details/fee-adviser-authority-details',
			  ),
		route: 'fee-adviser-authority-details',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/adviser-authority-details/employer-adviser-authority-details',
			  )
			: PLATFORM.moduleName(
					'forms/adviser-authority-details/employer-adviser-authority-details',
					'forms/adviser-authority-details/employer-adviser-authority-details',
			  ),
		route: 'employer-adviser-authority-details',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/rollover-history/rollover-history')
			: PLATFORM.moduleName(
					'forms/rollover-history/rollover-history',
					'forms/rollover-history/rollover-history',
			  ),
		route: 'rollover-history',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/adviser-authority-details/fund-adviser-authority-details',
			  )
			: PLATFORM.moduleName(
					'forms/adviser-authority-details/fund-adviser-authority-details',
					'forms/adviser-authority-details/fund-adviser-authority-details',
			  ),
		route: 'fund-adviser-authority-details',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/benefit-category-history/benefit-category-history',
			  )
			: PLATFORM.moduleName(
					'forms/benefit-category-history/benefit-category-history',
					'forms/benefit-category-history/benefit-category-history',
			  ),
		route: 'benefit-category-history',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/adviser-proportions/employer-adviser-proportions',
			  )
			: PLATFORM.moduleName(
					'forms/adviser-proportions/employer-adviser-proportions',
					'forms/adviser-proportions/employer-adviser-proportions',
			  ),
		route: 'employer-adviser-proportions',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/adviser-dealer-details/adviser-details',
			  )
			: PLATFORM.moduleName(
					'forms/adviser-dealer-details/adviser-details',
					'forms/adviser-dealer-details/adviser-details',
			  ),
		route: AppRouteID.AdviserDetails,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/adviser-dealer-details/dealer-details')
			: PLATFORM.moduleName(
					'forms/adviser-dealer-details/dealer-details',
					'forms/adviser-dealer-details/dealer-details',
			  ),
		route: AppRouteID.DealerDetails,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/adviser-proportions/member-adviser-proportions',
			  )
			: PLATFORM.moduleName(
					'forms/adviser-proportions/member-adviser-proportions',
					'forms/adviser-proportions/member-adviser-proportions',
			  ),
		route: 'member-adviser-proportions',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-bank-details/member-bank-details',
			  )
			: PLATFORM.moduleName(
					'forms/member-bank-details/member-bank-details',
					'forms/member-bank-details/member-bank-details',
			  ),
		route: 'member-bank-details',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/personal-details/personal-details')
			: PLATFORM.moduleName(
					'forms/personal-details/personal-details',
					'forms/personal-details/personal-details',
			  ),
		route: AppRouteID.PersonalDetails,
	}),
	createAppRoute({
		href: AppRouteID.FamilyLawSplitDetails,
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/family-law-split-details/family-law-split-details',
			  )
			: PLATFORM.moduleName(
					'forms/family-law-split-details/family-law-split-details',
					'forms/client-groups/family-law-split-details',
			  ),
		route: AppRouteID.FamilyLawSplitDetails,
	}),
	createAppRoute({
		href: AppRouteID.ClientGroups,
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/client-groups/client-groups')
			: PLATFORM.moduleName(
					'forms/client-groups/client-groups',
					'forms/client-groups/client-groups',
			  ),
		route: `${AppRouteID.ClientGroups}/:${FORM_SUBTYPE}`,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/contribution-rate-history/contribution-rate-history',
			  )
			: PLATFORM.moduleName(
					'forms/contribution-rate-history/contribution-rate-history',
					'forms/contribution-rate-history/contribution-rate-history',
			  ),
		route: 'contribution-rate-history',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/credentials/credentials')
			: PLATFORM.moduleName(
					'forms/credentials/credentials',
					'forms/credentials/credentials',
			  ),
		route: 'credentials',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/insurance-summary/insurance-summary')
			: PLATFORM.moduleName(
					'forms/insurance-summary/insurance-summary',
					'forms/insurance-summary/insurance-summary',
			  ),
		route: 'insurance-summary',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/interest-rate-history/interest-rate-history',
			  )
			: PLATFORM.moduleName(
					'forms/interest-rate-history/interest-rate-history',
					'forms/interest-rate-history/interest-rate-history',
			  ),
		route: AppRouteID.InterestRateHistory,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/view-ledgers/view-ledgers')
			: PLATFORM.moduleName(
					'forms/view-ledgers/view-ledgers',
					'forms/view-ledgers/view-ledgers',
			  ),
		route: 'ledgers-by-member',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/view-ledgers/view-ledgers')
			: PLATFORM.moduleName(
					'forms/view-ledgers/view-ledgers',
					'forms/view-ledgers/view-ledgers',
			  ),
		route: AppRouteID.LedgersByTransaction,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/journals-by-transaction/journals-by-transaction',
			  )
			: PLATFORM.moduleName(
					'forms/journals-by-transaction/journals-by-transaction',
					'forms/journals-by-transaction/journals-by-transaction',
			  ),
		route: AppRouteID.JournalsByTransaction,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/medical-history/medical-history')
			: PLATFORM.moduleName(
					'forms/medical-history/medical-history',
					'forms/medical-history/medical-history',
			  ),
		route: 'medical-history',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-and-client-details/member-and-client-details',
			  )
			: PLATFORM.moduleName(
					'forms/member-and-client-details/member-and-client-details',
					'forms/member-and-client-details/member-and-client-details',
			  ),
		route: AppRouteID.MemberDetails,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-beneficiaries/member-beneficiaries',
			  )
			: PLATFORM.moduleName(
					'forms/member-beneficiaries/member-beneficiaries',
					'forms/member-beneficiaries/member-beneficiaries',
			  ),
		route: AppRouteID.MemberBeneficiaries,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/investment-manager-details/investment-manager-details',
			  )
			: PLATFORM.moduleName(
					'forms/investment-manager-details/investment-manager-details',
					'forms/investment-manager-details/investment-manager-details',
			  ),
		route: 'investment-manager-details',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-investment-profile/adviser-model-investment-profile',
			  )
			: PLATFORM.moduleName(
					'forms/member-investment-profile/adviser-model-investment-profile',
					'forms/member-investment-profile/adviser-model-investment-profile',
			  ),
		route: 'adviser-model-investment-profile',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-investment-profile/bank-account-payment-proportions',
			  )
			: PLATFORM.moduleName(
					'forms/member-investment-profile/bank-account-payment-proportions',
					'forms/member-investment-profile/bank-account-payment-proportions',
			  ),
		route: 'bank-account-payment-proportions',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-investment-profile/category-investment-profile',
			  )
			: PLATFORM.moduleName(
					'forms/member-investment-profile/category-investment-profile',
					'forms/member-investment-profile/category-investment-profile',
			  ),
		route: 'category-investment-profile',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-investment-profile/cont-tax-investment-profile',
			  )
			: PLATFORM.moduleName(
					'forms/member-investment-profile/cont-tax-investment-profile',
					'forms/member-investment-profile/cont-tax-investment-profile',
			  ),
		route: 'cont-tax-investment-profile',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-investment-profile/contribution-investment-profile',
			  )
			: PLATFORM.moduleName(
					'forms/member-investment-profile/contribution-investment-profile',
					'forms/member-investment-profile/contribution-investment-profile',
			  ),
		route: 'contribution-investment-profile',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-investment-profile/employer-investment-profile',
			  )
			: PLATFORM.moduleName(
					'forms/member-investment-profile/employer-investment-profile',
					'forms/member-investment-profile/employer-investment-profile',
			  ),
		route: 'employer-investment-profile',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-investment-profile/fund-investment-profile',
			  )
			: PLATFORM.moduleName(
					'forms/member-investment-profile/fund-investment-profile',
					'forms/member-investment-profile/fund-investment-profile',
			  ),
		route: 'fund-investment-profile',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-investment-profile/fund-model-investment-profile',
			  )
			: PLATFORM.moduleName(
					'forms/member-investment-profile/fund-model-investment-profile',
					'forms/member-investment-profile/fund-model-investment-profile',
			  ),
		route: 'fund-model-investment-profile',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-investment-profile/group-investment-profile',
			  )
			: PLATFORM.moduleName(
					'forms/member-investment-profile/group-investment-profile',
					'forms/member-investment-profile/group-investment-profile',
			  ),
		route: 'group-investment-profile',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-investment-profile/member-investment-profile-2',
			  )
			: PLATFORM.moduleName(
					'forms/member-investment-profile/member-investment-profile-2',
					'forms/member-investment-profile/member-investment-profile-2',
			  ),
		route: 'member-investment-profile-2',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-investment-profile/member-investment-profile',
			  )
			: PLATFORM.moduleName(
					'forms/member-investment-profile/member-investment-profile',
					'forms/member-investment-profile/member-investment-profile',
			  ),
		route: 'member-investment-profile',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-investment-profile/pension-redemption-investment-profile',
			  )
			: PLATFORM.moduleName(
					'forms/member-investment-profile/pension-redemption-investment-profile',
					'forms/member-investment-profile/pension-redemption-investment-profile',
			  ),
		route: AppRouteID.PensionRedemptionInvestmentProfile,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-investment-profile/redemption-investment-profile',
			  )
			: PLATFORM.moduleName(
					'forms/member-investment-profile/redemption-investment-profile',
					'forms/member-investment-profile/redemption-investment-profile',
			  ),
		route: AppRouteID.RedemptionInvestmentProfile,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-investment-profile/rollover-request-investment-profile',
			  )
			: PLATFORM.moduleName(
					'forms/member-investment-profile/rollover-request-investment-profile',
					'forms/member-investment-profile/rollover-request-investment-profile',
			  ),
		route: 'rollover-request-investment-profile',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-investment-profile/transfer-investment-profile',
			  )
			: PLATFORM.moduleName(
					'forms/member-investment-profile/transfer-investment-profile',
					'forms/member-investment-profile/transfer-investment-profile',
			  ),
		route: 'transfer-investment-profile',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-investment-profile/user-defined-investment-profile',
			  )
			: PLATFORM.moduleName(
					'forms/member-investment-profile/user-defined-investment-profile',
					'forms/member-investment-profile/user-defined-investment-profile',
			  ),
		route: 'user-defined-investment-profile',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-payroll-history/member-payroll-history',
			  )
			: PLATFORM.moduleName(
					'forms/member-payroll-history/member-payroll-history',
					'forms/member-payroll-history/member-payroll-history',
			  ),
		route: 'member-payroll-history',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-salary-details/member-salary-details',
			  )
			: PLATFORM.moduleName(
					'forms/member-salary-details/member-salary-details',
					'forms/member-salary-details/member-salary-details',
			  ),
		route: 'member-salary-details',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/pension-summary/pension-summary')
			: PLATFORM.moduleName(
					'forms/pension-summary/pension-summary',
					'forms/pension-summary/pension-summary',
			  ),
		route: 'pension-summary',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/policy-benefit-details/policy-benefit-details',
			  )
			: PLATFORM.moduleName(
					'forms/policy-benefit-details/policy-benefit-details',
					'forms/policy-benefit-details/policy-benefit-details',
			  ),
		route: 'policy-benefit-details',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/payroll-details/payroll-details')
			: PLATFORM.moduleName(
					'forms/payroll-details/payroll-details',
					'forms/payroll-details/payroll-details',
			  ),
		route: AppRouteID.PayrollDetails,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/worker-dashboard/worker-dashboard')
			: PLATFORM.moduleName(
					'forms/worker-dashboard/worker-dashboard',
					'forms/worker-dashboard/worker-dashboard',
			  ),
		route: AppRouteID.WorkerDashboard,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/server-dashboard/server-dashboard')
			: PLATFORM.moduleName(
					'forms/server-dashboard/server-dashboard',
					'forms/server-dashboard/server-dashboard',
			  ),
		route: AppRouteID.ServerDashboard,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/payroll-location/payroll-location')
			: PLATFORM.moduleName(
					'forms/payroll-location/payroll-location',
					'forms/payroll-location/payroll-location',
			  ),
		route: AppRouteID.PayrollLocation,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/payroll-location/payroll-contact')
			: PLATFORM.moduleName(
					'forms/payroll-location/payroll-contact',
					'forms/payroll-location/payroll-contact',
			  ),
		route: AppRouteID.PayrollContact,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/payroll-location/payroll-center')
			: PLATFORM.moduleName(
					'forms/payroll-location/payroll-center',
					'forms/payroll-location/payroll-center',
			  ),
		route: AppRouteID.PayrollCenter,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/payroll-location/organiser-or-delegate',
			  )
			: PLATFORM.moduleName(
					'forms/payroll-location/organiser-or-delegate',
					'forms/payroll-location/organiser-or-delegate',
			  ),
		route: 'organiser-or-delegate',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/results-error-messages/results-error-messages',
			  )
			: PLATFORM.moduleName(
					'forms/results-error-messages/results-error-messages',
					'forms/results-error-messages/results-error-messages',
			  ),
		route: AppRouteID.TransactionMessages,
	}),
	createAppRoute({
		href: AppRouteID.SupplementaryDetails,
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/supplementary-details/supplementary-details',
			  )
			: PLATFORM.moduleName(
					'forms/supplementary-details/supplementary-details',
					'forms/supplementary-details/supplementary-details',
			  ),
		route: `${AppRouteID.SupplementaryDetails}/:${FORM_SUBTYPE}`,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transaction-centre/transaction-centre')
			: PLATFORM.moduleName(
					'forms/transaction-centre/transaction-centre',
					'forms/transaction-centre/transaction-centre',
			  ),
		route: AppRouteID.TransactionCentre,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/transaction-centre/transaction-headers/transaction-headers',
			  )
			: PLATFORM.moduleName(
					'forms/transaction-centre/transaction-headers/transaction-headers',
					'forms/transaction-centre/transaction-headers/transaction-headers',
			  ),
		route: AppRouteID.TransactionHeaders,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/transaction-centre/transaction-headers/transaction-headers-79',
			  )
			: PLATFORM.moduleName(
					'forms/transaction-centre/transaction-headers/transaction-headers-79',
					'forms/transaction-centre/transaction-headers/transaction-headers-79',
			  ),
		route: AppRouteID.TransactionHeaders + '-79',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/transaction-centre/transaction-headers/transaction-headers-77',
			  )
			: PLATFORM.moduleName(
					'forms/transaction-centre/transaction-headers/transaction-headers-77',
					'forms/transaction-centre/transaction-headers/transaction-headers-77',
			  ),
		route: AppRouteID.TransactionHeaders + '-77',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/transaction-centre/transaction-headers/transaction-headers-98',
			  )
			: PLATFORM.moduleName(
					'forms/transaction-centre/transaction-headers/transaction-headers-98',
					'forms/transaction-centre/transaction-headers/transaction-headers-98',
			  ),
		route: AppRouteID.TransactionHeaders + '-98',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/transaction-centre/transaction-lines/transaction-lines',
			  )
			: PLATFORM.moduleName(
					'forms/transaction-centre/transaction-lines/transaction-lines',
					'forms/transaction-centre/transaction-lines/transaction-lines',
			  ),
		route: AppRouteID.TransactionLines,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/transaction-centre/transaction-lines/transaction-lines-rollover',
			  )
			: PLATFORM.moduleName(
					'forms/transaction-centre/transaction-lines/transaction-lines-rollover',
					'forms/transaction-centre/transaction-lines/transaction-lines-rollover',
			  ),
		route: AppRouteID.TransactionLines + '-79',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/transaction-centre/transaction-lines/transaction-lines-rollover',
			  )
			: PLATFORM.moduleName(
					'forms/transaction-centre/transaction-lines/transaction-lines-rollover',
					'forms/transaction-centre/transaction-lines/transaction-lines-rollover',
			  ),
		route: AppRouteID.TransactionLines + '-9Z',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/transaction-centre/transaction-lines/transaction-lines-59',
			  )
			: PLATFORM.moduleName(
					'forms/transaction-centre/transaction-lines/transaction-lines-59',
					'forms/transaction-centre/transaction-lines/transaction-lines-59',
			  ),
		route: AppRouteID.TransactionLines + '-59',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-12')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-12',
					'forms/transactions/transaction-12',
			  ),
		route: 'transaction-12',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-13')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-13',
					'forms/transactions/transaction-13',
			  ),
		route: 'transaction-13',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-14')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-14',
					'forms/transactions/transaction-14',
			  ),
		route: 'transaction-14',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-61')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-61',
					'forms/transactions/transaction-61',
			  ),
		route: AppRouteID.Transaction61,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-98')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-98',
					'forms/transactions/transaction-98',
			  ),
		route: AppRouteID.Transaction98,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-85')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-85',
					'forms/transactions/transaction-85',
			  ),
		route: AppRouteID.Transaction85,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-77')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-77',
					'forms/transactions/transaction-77',
			  ),
		route: AppRouteID.Transaction77,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-79')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-79',
					'forms/transactions/transaction-79',
			  ),
		route: 'transaction-79',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-7A')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-7A',
					'forms/transactions/transaction-7A',
			  ),
		route: 'transaction-7A',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-7B')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-7B',
					'forms/transactions/transaction-7B',
			  ),
		route: 'transaction-7B',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-7C')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-7C',
					'forms/transactions/transaction-7C',
			  ),
		route: 'transaction-7C',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-7D')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-7D',
					'forms/transactions/transaction-7D',
			  ),
		route: 'transaction-7D',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-7E')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-7E',
					'forms/transactions/transaction-7E',
			  ),
		route: 'transaction-7E',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-7F')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-7F',
					'forms/transactions/transaction-7F',
			  ),
		route: 'transaction-7F',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-7G')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-7G',
					'forms/transactions/transaction-7G',
			  ),
		route: 'transaction-7G',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-7H')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-7H',
					'forms/transactions/transaction-7H',
			  ),
		route: 'transaction-7H',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-7I')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-7I',
					'forms/transactions/transaction-7I',
			  ),
		route: 'transaction-7I',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-7J')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-7J',
					'forms/transactions/transaction-7J',
			  ),
		route: 'transaction-7J',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-62')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-62',
					'forms/transactions/transaction-62',
			  ),
		route: AppRouteID.Transaction62,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-60')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-60',
					'forms/transactions/transaction-60',
			  ),
		route: AppRouteID.Transaction60,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-8U')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-8U',
					'forms/transactions/transaction-8U',
			  ),
		route: AppRouteID.Transaction8U,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transactions/transaction-8S')
			: PLATFORM.moduleName(
					'forms/transactions/transaction-8S',
					'forms/transactions/transaction-8S',
			  ),
		route: AppRouteID.Transaction8S,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-balance-history/member-balance-history',
			  )
			: PLATFORM.moduleName(
					'forms/member-balance-history/member-balance-history',
					'forms/member-balance-history/member-balance-history',
			  ),
		route: 'member-balance-history',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-eligible-service-history/member-eligible-service-history',
			  )
			: PLATFORM.moduleName(
					'forms/member-eligible-service-history/member-eligible-service-history',
					'forms/member-eligible-service-history/member-eligible-service-history',
			  ),
		route: 'member-eligible-service-history',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-tax-flags-history/member-tax-flags-history',
			  )
			: PLATFORM.moduleName(
					'forms/member-tax-flags-history/member-tax-flags-history',
					'forms/member-tax-flags-history/member-tax-flags-history',
			  ),
		route: 'member-tax-flags-history',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/working-hours-history/working-hours-history',
			  )
			: PLATFORM.moduleName(
					'forms/working-hours-history/working-hours-history',
					'forms/working-hours-history/working-hours-history',
			  ),
		route: 'working-hours-history',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-taxable-salary-history/member-taxable-salary-history',
			  )
			: PLATFORM.moduleName(
					'forms/member-taxable-salary-history/member-taxable-salary-history',
					'forms/member-taxable-salary-history/member-taxable-salary-history',
			  ),
		route: 'member-taxable-salary-history',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/working-status-history/working-status-history',
			  )
			: PLATFORM.moduleName(
					'forms/working-status-history/working-status-history',
					'forms/working-status-history/working-status-history',
			  ),
		route: 'working-status-history',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/batch-processing-centre/batch-processing-centre',
			  )
			: PLATFORM.moduleName(
					'forms/batch-processing-centre/batch-processing-centre',
			  ),
		route: AppRouteID.BatchProcessingCentre,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/benefits-payments-centre/benefits-payments-centre',
			  )
			: PLATFORM.moduleName(
					'forms/benefits-payments-centre/benefits-payments-centre',
			  ),
		route: AppRouteID.BenefitsPaymentsCentre,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/user-details/user-details')
			: PLATFORM.moduleName(
					'forms/user-details/user-details',
					'forms/user-details/user-details',
			  ),
		route: AppRouteID.UserDetails,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/user-details/web-user-security')
			: PLATFORM.moduleName(
					'forms/user-details/web-user-security',
					'forms/user-details/web-user-security',
			  ),
		route: AppRouteID.WebUserSecurity,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/customisation-editor/customisation-editor',
			  )
			: PLATFORM.moduleName(
					'forms/customisation-editor/customisation-editor',
					'forms/customisation-editor/customisation-editor',
			  ),
		route: AppRouteID.CustomisationEditor,
		href: '#customisation-editor',
		settings: {
			data: {
				hideOnFormCatalog: true,
			},
		},
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('views/error-404-page/error-404-page')
			: PLATFORM.moduleName(
					'views/error-404-page/error-404-page',
					'views/error-404-page/error-404-page',
			  ),
		route: 'error-404',
		settings: {
			data: {
				hideOnFormCatalog: true,
			},
		},
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('views/error-page/error-page')
			: PLATFORM.moduleName(
					'views/error-page/error-page',
					'views/error-page/error-page',
			  ),
		route: 'error',
		settings: {
			data: {
				hideOnFormCatalog: true,
			},
		},
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('views/blank-page/blank-page.html')
			: PLATFORM.moduleName(
					'views/blank-page/blank-page.html',
					'views/blank-page/blank-page.html',
			  ),
		route: AppRouteID.Blank,
		settings: {
			data: {
				hideOnFormCatalog: true,
			},
		},
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/receipt-processing-centre/receipt-processing-centre',
			  )
			: PLATFORM.moduleName(
					'forms/receipt-processing-centre/receipt-processing-centre',
					'views/receipt-processing-centre/receipt-processing-centre',
			  ),
		route: 'receipt-processing-centre',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/receipt-processing-centre/new-receipt/new-receipt',
			  )
			: PLATFORM.moduleName(
					'forms/receipt-processing-centre/new-receipt/new-receipt',
					'views/receipt-processing-centre/new-receipt/new-receipt',
			  ),
		route: AppRouteID.NewReceipt,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/reporting-dashboard/reporting-centre')
			: PLATFORM.moduleName(
					'forms/reporting-dashboard/reporting-centre',
					'forms/reporting-dashboard/reporting-centre',
			  ),
		route: 'reporting-centre',
	}),
	createAppRoute({
		href: AppRouteID.Report,
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/reporting-dashboard/report')
			: PLATFORM.moduleName(
					'forms/reporting-dashboard/report',
					'forms/reporting-dashboard/report',
			  ),
		route: `${AppRouteID.Report}/:${FORM_SUBTYPE}`,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/asset-transaction-list/asset-transaction-list',
			  )
			: PLATFORM.moduleName(
					'forms/asset-transaction-list/asset-transaction-list',
					'forms/asset-transaction-list/asset-transaction-list',
			  ),
		route: AppRouteID.AssetTransactionList,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/form-catalogue/form-catalogue')
			: PLATFORM.moduleName(
					'forms/form-catalogue/form-catalogue',
					'forms/form-catalogue/form-catalogue',
			  ),
		route: 'form-catalogue',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/insurance-options/insurance-options')
			: PLATFORM.moduleName(
					'forms/insurance-options/insurance-options',
					'forms/insurance-options/insurance-options',
			  ),
		route: 'insurance-options',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/asset-allocation/asset-allocation')
			: PLATFORM.moduleName(
					'forms/asset-allocation/asset-allocation',
					'forms/asset-transaction-list/asset-allocation',
			  ),
		route: 'asset-allocation',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/transaction-list/transaction-list')
			: PLATFORM.moduleName(
					'forms/transaction-list/transaction-list',
					'forms/transaction-list/transaction-list',
			  ),
		route: 'transaction-list',
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-benefit-quotes/member-benefit-quotes',
			  )
			: PLATFORM.moduleName(
					'forms/member-benefit-quotes/member-benefit-quotes',
					'forms/member-benefit-quotes/member-benefit-quotes',
			  ),
		route: AppRouteID.MemberBenefitQuotes,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/receipt-refund-details/receipt-refund-details',
			  )
			: PLATFORM.moduleName(
					'forms/receipt-refund-details/receipt-refund-details',
					'forms/receipt-refund-details/receipt-refund-details',
			  ),
		route: AppRouteID.ReceiptRefundDetails,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-payment-deduction-history/member-payment-deduction-history',
			  )
			: PLATFORM.moduleName(
					'forms/member-payment-deduction-history/member-payment-deduction-history',
					'forms/member-payment-deduction-history/member-payment-deduction-history',
			  ),
		route: AppRouteID.MemberPaymentDeductionHistory,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-payment-schedule-history/member-payment-schedule-history',
			  )
			: PLATFORM.moduleName(
					'forms/member-payment-schedule-history/member-payment-schedule-history',
					'forms/member-payment-schedule-history/member-payment-schedule-history',
			  ),
		route: AppRouteID.MemberPaymentScheduleHistory,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/record-authorisations/record-authorisations',
			  )
			: PLATFORM.moduleName(
					'forms/record-authorisations/record-authorisations',
					'forms/record-authorisations/record-authorisations',
			  ),
		route: AppRouteID.RecordAuthorisations,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/merge-history/merge-history')
			: PLATFORM.moduleName(
					'forms/merge-history/merge-history',
					'forms/merge-history/merge-history',
			  ),
		route: AppRouteID.MergeHistory,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/cheque-eft-information/cheque-eft-information',
			  )
			: PLATFORM.moduleName(
					'forms/cheque-eft-information/cheque-eft-information',
					'forms/cheque-eft-information/cheque-eft-information',
			  ),
		route: AppRouteID.ChequeEftInformation,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/payroll-rates-history/payroll-rates-history',
			  )
			: PLATFORM.moduleName(
					'forms/payroll-rates-history/payroll-rates-history',
					'forms/payroll-rates-history/payroll-rates-history',
			  ),
		route: AppRouteID.PayrollRatesHistory,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/treaty-expense-codes/treaty-expense-codes',
			  )
			: PLATFORM.moduleName(
					'forms/treaty-expense-codes/treaty-expense-codes',
					'forms/treaty-expense-codes/treaty-expense-codes',
			  ),
		route: AppRouteID.TreatyExpenseCodes,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/treaty-configuration/treaty-configuration',
			  )
			: PLATFORM.moduleName(
					'forms/treaty-configuration/treaty-configuration',
					'forms/treaty-configuration/treaty-configuration',
			  ),
		route: AppRouteID.TreatyConfiguration,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/verification-history/verification-history',
			  )
			: PLATFORM.moduleName(
					'forms/verification-history/verification-history',
					'forms/verification-history/verification-history',
			  ),
		route: AppRouteID.VerificationHistory,
	}),
	createAppRoute({
		href: AppRouteID.AddressDetails,
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/address-details/address-details')
			: PLATFORM.moduleName(
					'forms/address-details/address-details',
					'forms/address-details/address-details',
			  ),
		route: `${AppRouteID.AddressDetails}/:${FORM_SUBTYPE}`,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/contribution-files/contribution-files')
			: PLATFORM.moduleName(
					'forms/contribution-files/contribution-files',
					'forms/contribution-files/contribution-files',
			  ),
		route: AppRouteID.ContributionFiles,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/static-data-lines/static-data-lines')
			: PLATFORM.moduleName(
					'forms/static-data-lines/static-data-lines',
					'forms/static-data-lines/static-data-lines',
			  ),
		route: AppRouteID.StaticDataLines,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/workbench-lines/workbench-lines')
			: PLATFORM.moduleName(
					'forms/workbench-lines/workbench-lines',
					'forms/workbench-lines/workbench-lines',
			  ),
		route: AppRouteID.WorkbenchLines,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/contribution-lines/contribution-lines')
			: PLATFORM.moduleName(
					'forms/contribution-lines/contribution-lines',
					'forms/contribution-lines/contribution-lines',
			  ),
		route: AppRouteID.ContributionLines,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/errors-and-exceptions/errors-and-exceptions',
			  )
			: PLATFORM.moduleName(
					'forms/errors-and-exceptions/errors-and-exceptions',
					'forms/errors-and-exceptions/errors-and-exceptions',
			  ),
		route: AppRouteID.ErrorsAndExceptions,
	}),
	createAppRoute({
		href: AppRouteID.ExceptionHistory,
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/exception-history/exception-history')
			: PLATFORM.moduleName(
					'forms/exception-history/exception-history',
					'forms/exception-history/exception-history',
			  ),
		route: `${AppRouteID.ExceptionHistory}/:${FORM_SUBTYPE}`,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/member-payment-history/member-payment-history',
			  )
			: PLATFORM.moduleName(
					'forms/member-payment-history/member-payment-history',
					'forms/member-payment-history/member-payment-history',
			  ),
		route: AppRouteID.MemberPaymentHistory,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/transfer-request-lines/transfer-request-lines',
			  )
			: PLATFORM.moduleName(
					'forms/transfer-request-lines/transfer-request-lines',
					'forms/transfer-request-lines/transfer-request-lines',
			  ),
		route: AppRouteID.TransferRequestLines,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/investment-history/investment-history')
			: PLATFORM.moduleName(
					'forms/investment-history/investment-history',
					'forms/investment-history/investment-history',
			  ),
		route: AppRouteID.InvestmentHistory,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/redemption-history/redemption-history')
			: PLATFORM.moduleName(
					'forms/redemption-history/redemption-history',
					'forms/redemption-history/redemption-history',
			  ),
		route: AppRouteID.RedemptionHistory,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/superstream-manual-matches/superstream-manual-matches',
			  )
			: PLATFORM.moduleName(
					'forms/superstream-manual-matches/superstream-manual-matches',
					'forms/superstream-manual-matches/superstream-manual-matches',
			  ),
		route: AppRouteID.SuperStreamManualMatches,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/unit-price-history/unit-price-history')
			: PLATFORM.moduleName(
					'forms/unit-price-history/unit-price-history',
					'forms/unit-price-history/unit-price-history',
			  ),
		route: AppRouteID.UnitPriceHistory,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/dealer-history/dealer-history')
			: PLATFORM.moduleName(
					'forms/dealer-history/dealer-history',
					'forms/dealer-history/dealer-history',
			  ),
		route: AppRouteID.DealerHistory,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/adviser-history/adviser-history')
			: PLATFORM.moduleName(
					'forms/adviser-history/adviser-history',
					'forms/adviser-history/adviser-history',
			  ),
		route: AppRouteID.AdviserHistory,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/corporate-action-elections/corporate-action-elections',
			  )
			: PLATFORM.moduleName(
					'forms/corporate-action-elections/corporate-action-elections',
					'forms/corporate-action-elections/corporate-action-elections',
			  ),
		route: AppRouteID.CorporateActionElections,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/bank-statement-reconciliation/bank-statement-reconciliation',
			  )
			: PLATFORM.moduleName(
					'forms/bank-statement-reconciliation/bank-statement-reconciliation',
					'forms/bank-statement-reconciliation/bank-statement-reconciliation',
			  ),
		route: AppRouteID.BankStatementReconciliation,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/income-election-history/income-election-history',
			  )
			: PLATFORM.moduleName(
					'forms/income-election-history/income-election-history',
					'forms/income-election-history/income-election-history',
			  ),
		route: AppRouteID.IncomeElectionHistory,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/corporate-action/corporate-action')
			: PLATFORM.moduleName(
					'forms/corporate-action/corporate-action',
					'forms/corporate-action/corporate-action',
			  ),
		route: AppRouteID.CorporateAction,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/api-audit/api-audit')
			: PLATFORM.moduleName(
					'forms/api-audit/api-audit',
					'forms/api-audit/api-audit',
			  ),
		route: AppRouteID.ApiAudit,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/suppression-details/suppression-details',
			  )
			: PLATFORM.moduleName(
					'forms/suppression-details/suppression-details',
					'forms/suppression-details/suppression-details',
			  ),
		route: AppRouteID.SuppressionDetails,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName('forms/suppression-issues/suppression-issues')
			: PLATFORM.moduleName(
					'forms/suppression-issues/suppression-issues',
					'forms/suppression-issues/suppression-issues',
			  ),
		route: AppRouteID.SuppressionIssues,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/data-retention-control/data-retention-control',
			  )
			: PLATFORM.moduleName(
					'forms/data-retention-control/data-retention-control',
					'forms/data-retention-control/data-retention-control',
			  ),
		route: AppRouteID.DataRetentionControl,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/client-deidentification/client-deidentification',
			  )
			: PLATFORM.moduleName(
					'forms/client-deidentification/client-deidentification',
					'forms/client-deidentification/client-deidentification',
			  ),
		route: AppRouteID.ClientDeidentification,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/data-retention-processing/data-retention-processing',
			  )
			: PLATFORM.moduleName(
					'forms/data-retention-processing/data-retention-processing',
					'forms/data-retention-processing/data-retention-processing',
			  ),
		route: AppRouteID.DataRetentionProcessing,
	}),
	createAppRoute({
		moduleId: DEVELOPMENT
			? PLATFORM.moduleName(
					'forms/deidentified-clients/deidentified-clients',
			  )
			: PLATFORM.moduleName(
					'forms/deidentified-clients/deidentified-clients',
					'forms/deidentified-clients/deidentified-clients',
			  ),
		route: AppRouteID.DeidentifiedClients,
	}),
];
